import { z } from 'zod';

export const SourceTypeSchema = z.enum([
  'official',
  'filing',
  'market',
  'article',
  'tweet',
  'news',
  'model_output',
]);

export const TrustTierSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
]);

export const SourceRefSchema = z.object({
  sourceId: z.string(),
  sourceType: SourceTypeSchema,
  title: z.string(),
  canonicalUri: z.string(),
  authorOrIssuer: z.string().optional(),
  publishedAt: z.string().optional(),
  effectiveAt: z.string().optional(),
  fetchedAt: z.string(),
  knownAt: z.string(),
  contentHash: z.string(),
  trustTier: TrustTierSchema,
  access: z.enum(['public', 'private_corpus', 'licensed', 'user_provided']),
  supersedesSourceId: z.string().optional(),
});

export type SourceRef = z.infer<typeof SourceRefSchema>;

export const ClaimClassificationSchema = z.enum([
  'FACT',
  'INFERENCE',
  'OPINION',
  'FORECAST',
]);

export const ClaimSchema = z.object({
  claimId: z.string(),
  subject: z.string(),
  predicate: z.string(),
  object: z.unknown(),
  classification: ClaimClassificationSchema,
  confidence: z.number().min(0).max(1),
  validFrom: z.string().optional(),
  validTo: z.string().optional(),
  knownAt: z.string(),
  sourceIds: z.array(z.string()).min(1),
  supportingClaimIds: z.array(z.string()).default([]),
  contradictingClaimIds: z.array(z.string()).default([]),
  status: z.enum(['active', 'superseded', 'disputed', 'falsified']),
});

export type Claim = z.infer<typeof ClaimSchema>;

export const BitemporalFieldsSchema = z.object({
  validAt: z.string(),
  knownAt: z.string(),
  ingestedAt: z.string(),
  revisedAt: z.string().optional(),
});

export const ObservationSchema = z.object({
  observationId: z.string(),
  seriesId: z.string(),
  value: z.union([z.number(), z.string(), z.boolean(), z.null()]),
  unit: z.string().optional(),
  currency: z.string().optional(),
  validAt: z.string(),
  knownAt: z.string(),
  ingestedAt: z.string(),
  revisedAt: z.string().optional(),
  sourceId: z.string(),
  quality: z.enum(['complete', 'partial', 'stale', 'unavailable']),
  glossaryTermId: z.string(),
});

export type Observation = z.infer<typeof ObservationSchema>;

export const DataQualityStatusSchema = z.enum([
  'complete',
  'partial',
  'stale',
  'unavailable',
  'contradicted',
]);

export type DataQualityStatus = z.infer<typeof DataQualityStatusSchema>;
